import equal from 'fast-deep-equal';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

export function useSelectedVariant(product) {
  const { query } = useRouter();

  const productPageReady = !!product && product.handle === query.handle;
  const hasOneVariant = product.variants?.length === 1;
  const isNonGroupedProductWithMultiColors =
    !product?.grouping?.isTransformed && product?.optionsMap?.Color?.length > 1;

  const [selectedVariant, setSelectedVariant] = useState(
    productPageReady && hasOneVariant ? product.variants[0] : null
  );
  const [selectedOptions, setSelectedOptions] = useState(
    productPageReady && hasOneVariant
      ? product.variants[0].selectedOptionsMap
      : null
  );
  const [selectedMedia, setSelectedMedia] = useState(
    isNonGroupedProductWithMultiColors || !productPageReady
      ? null
      : product?.media
  );

  // set initial selected options on mount unless has one variant
  const setInitialSelectedOptionsOnProductMount = useCallback(() => {
    if (!productPageReady) {
      setSelectedOptions(null);
      return;
    }

    if (product.variants?.length === 1 && selectedOptions) {
      // Fix PACK bug when visiting a product page from a product page, and only 1 variant exists
      setSelectedVariant(product.variants[0]);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const queriedVariantId = params.get('variant');
    let queriedVariant = null;
    if (queriedVariantId) {
      queriedVariant = product.variants.find(
        ({ legacyResourceId }) => legacyResourceId === queriedVariantId
      );
    }
    if (queriedVariant) {
      setSelectedOptions(queriedVariant.selectedOptionsMap);
    } else {
      const firstAvailableVariant = product.variants.find(
        ({ availableForSale }) => availableForSale
      );

      if (firstAvailableVariant) {
        setSelectedOptions(firstAvailableVariant.selectedOptionsMap);
      } else {
        // If no variants are available, fall back to the first variant
        setSelectedOptions(product.variants[0]?.selectedOptionsMap || null);
      }
    }
  }, [query.handle, product?.id]);

  // set selected variant from selected options unless has one variant
  const setSelectedVariantFromSelectedOptions = useCallback(() => {
    if (!productPageReady || !selectedOptions) {
      setSelectedVariant(null);
      return;
    }

    if (product.variants?.length === 1 && selectedVariant) return;

    const selectedVariantFromOptions = product.variants.find(
      ({ selectedOptionsMap }) => {
        return equal(selectedOptions, selectedOptionsMap);
      }
    );
    setSelectedVariant(selectedVariantFromOptions || null);
  }, [query.handle, product?.id, selectedOptions, selectedVariant?.id]);

  // set variant url param on selected variant change unless has one variant
  const setVariantUrlParamOnSelectedVariantChange = useCallback(() => {
    if (!productPageReady) return;

    if (product.variants?.length === 1 || !selectedVariant) return;

    const { origin, search } = window.location;

    const params = new URLSearchParams(search);
    params.set('variant', selectedVariant.legacyResourceId);

    const updatedUrl = `${origin}/products/${selectedVariant.product.handle}?${params}`;

    window.history.replaceState(window.history.state, '', updatedUrl);
  }, [query.handle, product?.id, selectedVariant?.id]);

  // set selected media on selected variant change
  const setSelectedMediaOnSelectedVariantChange = useCallback(() => {
    if (!productPageReady) {
      setSelectedMedia(null);
      return;
    }

    const options = Object.keys(product.optionsMap || {});
    const variants = options
      .map((option) => product.optionsMap[option])
      .flat()
      .map((option) => option.toLowerCase());

    function getFilteredMedia(media) {
      if (!variants.includes(media.alt.toLowerCase())) return media;
      if (media.alt.toLowerCase() === selectedVariant?.title.toLowerCase())
        return media;
      return null;
    }

    const newMedia = product.media
      ?.map(getFilteredMedia)
      .filter((media) => media);

    setSelectedMedia(newMedia);

    // I'M LEAVING BELOW CODE IN CASE WE NEED TO REVERT BACK TO IT
    // OR IF WE NEED TO ADD MULTIPLE FILTER SUPPORT IN THE FUTURE

    // // filter media by color via possible alt tag if product is not grouped and has multiple colors
    // if (
    //   (product.optionsMap.Color || product.optionsMap.Size) &&
    //   selectedVariant
    // ) {
    //   // Set up our list of available color options on the primary product
    //   const colorOptions = product.optionsMap?.Color?.map((color) =>
    //     color.toLowerCase()
    //   );
    //   // Set up our list of available size options on the primary product
    //   const sizeOptions = product.optionsMap?.Size?.map((size) =>
    //     size.toLowerCase()
    //   );

    //   // Based on the current selected variant, what color and size are we viewing?
    //   const selectedColor =
    //     selectedVariant.selectedOptionsMap.Color?.toLowerCase();
    //   const selectedSize =
    //     selectedVariant.selectedOptionsMap.Size?.toLowerCase();

    //   // Next we'll filter on the alt text to see if it matches a color or size value
    //   let filteredMedia = [];

    //   // If both Sizes & Colors are available on this product filter on both.
    //   if (
    //     product.optionsMap?.Color?.length > 0 &&
    //     product.optionsMap?.Size?.length > 0
    //   ) {
    //     filteredMedia = product.media.filter((item) => {
    //       const alt = item.alt?.trim().toLowerCase();

    //       // Includes all images containing "color / size" OR contains no "/" in it.
    //       return (
    //         alt === `${selectedColor} / ${selectedSize}` || !alt.includes('/')
    //       );
    //     });
    //   }

    //   // If only color options are available, then filter those.
    //   else if (product.optionsMap?.Color?.length > 0) {
    //     filteredMedia = product.media.filter((item) => {
    //       const alt = item.alt?.trim().toLowerCase();
    //       return alt === selectedColor || !colorOptions.includes(alt);
    //     });
    //   }

    //   // If only size options are available, then filter those.
    //   else if (product.optionsMap?.Size?.length > 0) {
    //     filteredMedia = product.media.filter((item) => {
    //       const alt = item.alt?.trim().toLowerCase();
    //       return alt === selectedSize || !sizeOptions.includes(alt);
    //     });
    //   }

    //   console.log('filteredMedia', filteredMedia);

    //   setSelectedMedia(filteredMedia);
    // } else {
    //   console.log('filteredMedia', product.media);

    //   setSelectedMedia(product.media);
    // }
  }, [query.handle, product?.id, selectedMedia, selectedVariant?.id]);

  useEffect(() => {
    setInitialSelectedOptionsOnProductMount();
  }, [product?.id]);

  useEffect(() => {
    setSelectedVariantFromSelectedOptions();
  }, [selectedOptions]);

  useEffect(() => {
    setVariantUrlParamOnSelectedVariantChange();
  }, [selectedVariant?.id]);

  useEffect(() => {
    setSelectedMediaOnSelectedVariantChange();
  }, [selectedVariant?.id]);

  return {
    selectedOptions,
    selectedVariant,
    setSelectedOptions,
    selectedMedia,
  };
}
